import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePartnerDetails } from '@/hooks/usePartnerDetails';

interface ProtectedPartnerRouteProps {
  children: React.ReactNode;
  requireWhiteLabel?: boolean;
}

export default function ProtectedPartnerRoute({ 
  children, 
  requireWhiteLabel = false 
}: ProtectedPartnerRouteProps) {
  const { session, user, loading: authLoading, profile } = useAuth();
  const { data: partnerDetails, isLoading: partnerLoading } = usePartnerDetails();
  const location = useLocation();

  // Show loading while auth is being determined
  if (authLoading || partnerLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!session || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user is an actual partner (not a sub-account)
  const isSubAccount = profile?.user_type === 'partner_client';
  const isActualPartner = partnerDetails !== null && !isSubAccount;
  
  if (!isActualPartner) {
    return <Navigate to="/dashboard" replace />;
  }

  // If route requires white-label access, check partner type
  if (requireWhiteLabel && partnerDetails?.partner_type !== 'white_label') {
    // Co-branded partners trying to access white-label routes
    return <Navigate to="/partner/dashboard" replace />;
  }

  return <>{children}</>;
}
